import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Lock, Circle, Trophy, Star, Zap, Target, Flame, Trash2, AlertTriangle, Filter, Clock, Crown } from 'lucide-react';
import { challengesAPI, userAPI } from '../services/api';
import useUserStore from '../store/userStore';
import { toast } from 'react-toastify';

const DIFFICULTY_CONFIG = {
  beginner: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-500/50',
    glow: 'shadow-emerald-500/20',
    icon: Target,
    label: 'Beginner'
  },
  intermediate: {
    bg: 'bg-amber-500',
    border: 'border-amber-500/50',
    glow: 'shadow-amber-500/20',
    icon: Zap,
    label: 'Intermediate'
  },
  advanced: {
    bg: 'bg-red-500',
    border: 'border-red-500/50',
    glow: 'shadow-red-500/20',
    icon: Flame,
    label: 'Advanced'
  },
};

const ChallengeTile = ({ challenge, isCompleted, isUnlocked, bestScore, onSelect, isPremiumLocked, isPublicView }) => {
  const config = DIFFICULTY_CONFIG[challenge.difficulty] || DIFFICULTY_CONFIG.beginner;
  const DifficultyIcon = config.icon;

  // In public view, all challenges appear unlocked and clickable
  const effectivelyUnlocked = isPublicView || isUnlocked;
  const effectivelyPremiumLocked = isPublicView ? false : isPremiumLocked;

  // Special styling for lead magnet and free challenges
  const isLeadMagnet = challenge.is_lead_magnet;
  const isFreeChallenge = challenge.is_free || challenge.is_lead_magnet;

  return (
    <div
      onClick={() => onSelect(challenge, effectivelyPremiumLocked)}
      className={`
        relative p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer
        ${isFreeChallenge
          ? 'border-green-500 bg-gradient-to-br from-green-900/30 to-emerald-900/20 hover:scale-[1.02] hover:shadow-lg hover:shadow-green-500/20 ring-1 ring-green-500/30'
          : isCompleted
            ? 'border-green-500/50 bg-gradient-to-br from-green-900/40 to-emerald-900/30'
            : effectivelyUnlocked && !effectivelyPremiumLocked
              ? `${config.border} bg-slate-800/80 hover:scale-[1.02] hover:shadow-lg ${config.glow}`
              : effectivelyPremiumLocked
                ? 'border-amber-500/30 bg-slate-800/60 hover:border-amber-500/50'
                : 'border-slate-700 bg-slate-800/40 opacity-50'
        }
      `}
    >
      {/* FREE Badge for Lead Magnet and Free Challenges */}
      {isFreeChallenge && (
        <div className="absolute -top-3 -left-3 z-10">
          <div className="px-3 py-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold shadow-lg">
            {isLeadMagnet ? '✨ TRY FREE' : '🆓 FREE'}
          </div>
        </div>
      )}

      {/* Premium Lock Badge */}
      {effectivelyPremiumLocked && !isFreeChallenge && (
        <div className="absolute -top-2 -left-2 z-10">
          <div className="w-7 h-7 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
            <Crown className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      )}

      {/* Status Badge */}
      <div className={`
        absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center
        ${isCompleted ? 'bg-green-500' : effectivelyUnlocked && !effectivelyPremiumLocked ? 'bg-blue-500' : 'bg-slate-600'}
      `}>
        {isCompleted ? (
          <CheckCircle className="w-4 h-4 text-white" />
        ) : effectivelyUnlocked && !effectivelyPremiumLocked ? (
          <Circle className="w-4 h-4 text-white" />
        ) : (
          <Lock className="w-3.5 h-3.5 text-slate-400" />
        )}
      </div>

      {/* Difficulty Badge */}
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white mb-2 ${config.bg}`}>
        <DifficultyIcon className="w-3 h-3" />
        {config.label}
        {effectivelyPremiumLocked && <span className="ml-1 text-amber-200">• Premium</span>}
      </div>

      {/* Title */}
      <h3 className={`font-semibold text-sm mb-3 line-clamp-2 min-h-[2.5rem] ${effectivelyUnlocked ? 'text-white' : 'text-slate-500'}`}>
        {challenge.title}
      </h3>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-yellow-500">
          <Zap className="w-3.5 h-3.5" />
          <span className="font-bold">{challenge.base_reward} DP</span>
        </div>

        {challenge.estimated_time_minutes && (
          <div className="flex items-center gap-1 text-slate-500">
            <Clock className="w-3 h-3" />
            <span>{challenge.estimated_time_minutes}m</span>
          </div>
        )}
      </div>

      {/* Score Badge */}
      {bestScore > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-700 flex items-center gap-1">
          <Star className="w-3 h-3 text-yellow-500" />
          <span className="text-xs text-slate-400">Best: {bestScore}</span>
        </div>
      )}
    </div>
  );
};

// Premium Upgrade Prompt Modal
const UpgradePromptModal = ({ onClose }) => {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-700"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Premium Challenge</h3>
          <p className="text-slate-400 mb-6">
            Intermediate and Advanced challenges require a Premium subscription. Upgrade to access all 50+ system design challenges!
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgressTree = ({ onChallengeSelect, isPublicView = false }) => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, beginner, intermediate, advanced, unlocked, completed
  const { token, user } = useUserStore();

  useEffect(() => {
    loadProgressTree();
  }, []);

  const loadProgressTree = async () => {
    try {
      setLoading(true);

      let challengeData, questStats;

      if (token === 'demo_token') {
        // Demo mode mock data
        challengeData = [
          { id: 1, title: 'Design a URL Shortener', difficulty: 'beginner', base_reward: 100, estimated_time_minutes: 30, user_unlocked: true, user_completed: false, user_best_score: 0 },
          { id: 2, title: 'Design an API Rate Limiter', difficulty: 'intermediate', base_reward: 150, estimated_time_minutes: 35, user_unlocked: false, user_completed: false, user_best_score: 0 },
          { id: 3, title: 'Design a Distributed Cache', difficulty: 'intermediate', base_reward: 200, estimated_time_minutes: 40, user_unlocked: false, user_completed: false, user_best_score: 0 },
        ];
        questStats = { total: 3, unlocked: 1, completed: 0 };
      } else {
        const response = await challengesAPI.getTree();

        if (!response.data) {
          throw new Error('Invalid response from server');
        }

        challengeData = Array.isArray(response.data.challenges) ? response.data.challenges : [];
        questStats = response.data.stats || { total: 0, unlocked: 0, completed: 0 };
      }

      setChallenges(challengeData);
      setStats(questStats);
    } catch (error) {
      console.error('Error loading progress tree:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const filteredChallenges = useMemo(() => {
    let result = [...challenges];

    switch (filter) {
      case 'beginner':
      case 'intermediate':
      case 'advanced':
        result = result.filter(c => c.difficulty === filter);
        break;
      case 'unlocked':
        result = result.filter(c => c.user_unlocked && !c.user_completed);
        break;
      case 'completed':
        result = result.filter(c => c.user_completed);
        break;
      default:
        break;
    }

    // Sort by: lead magnet first, then free, then unlocked, then by difficulty, then by id
    const difficultyOrder = { beginner: 0, intermediate: 1, advanced: 2 };
    return result.sort((a, b) => {
      // Lead magnet (featured free trial) challenges always first
      if (a.is_lead_magnet !== b.is_lead_magnet) return a.is_lead_magnet ? -1 : 1;
      // Free challenges second
      if (a.is_free !== b.is_free) return a.is_free ? -1 : 1;
      // Completed first, then unlocked, then locked
      if (a.user_completed !== b.user_completed) return b.user_completed ? 1 : -1;
      if (a.user_unlocked !== b.user_unlocked) return b.user_unlocked ? 1 : -1;
      // Then by difficulty
      if (difficultyOrder[a.difficulty] !== difficultyOrder[b.difficulty]) {
        return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
      }
      // Then by id
      return a.id - b.id;
    });
  }, [challenges, filter]);

  // Check if challenge is premium locked for user
  const isChallengePremiumLocked = (challenge) => {
    // If user is premium, nothing is locked
    if (user?.is_premium) return false;
    // Use backend flags if available
    if (challenge.can_access !== undefined) return !challenge.can_access;
    if (challenge.is_premium_required !== undefined) return challenge.is_premium_required;
    // Fallback: intermediate and advanced are premium
    return challenge.difficulty === 'intermediate' || challenge.difficulty === 'advanced';
  };

  const handleChallengeSelect = (challenge, isPremiumLocked) => {
    // For public view, always allow clicking - parent handles routing
    if (isPublicView) {
      if (onChallengeSelect) {
        onChallengeSelect(challenge);
      }
      return;
    }

    if (isPremiumLocked) {
      setShowUpgradeModal(true);
      return;
    }
    if (!challenge.user_unlocked) {
      toast.info('Complete prerequisite challenges to unlock this one');
      return;
    }
    if (onChallengeSelect) {
      onChallengeSelect(challenge);
    }
  };

  const handleClearProgress = async () => {
    if (token === 'demo_token') {
      toast.info('Progress reset is not available in demo mode');
      setShowClearConfirm(false);
      return;
    }

    try {
      setClearing(true);
      await userAPI.clearProgress();
      toast.success('Quest progress cleared! Starting fresh.');
      setShowClearConfirm(false);
      loadProgressTree();
    } catch (error) {
      console.error('Error clearing progress:', error);
      toast.error('Failed to clear progress. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading challenges...</div>
      </div>
    );
  }

  const progressPercent = stats ? Math.round((stats.completed / stats.total) * 100) || 0 : 0;

  const filterButtons = [
    { key: 'all', label: 'All', count: challenges.length },
    { key: 'beginner', label: 'Beginner', count: challenges.filter(c => c.difficulty === 'beginner').length, color: 'bg-emerald-500' },
    { key: 'intermediate', label: 'Intermediate', count: challenges.filter(c => c.difficulty === 'intermediate').length, color: 'bg-amber-500' },
    { key: 'advanced', label: 'Advanced', count: challenges.filter(c => c.difficulty === 'advanced').length, color: 'bg-red-500' },
    // Only show these for authenticated users
    ...(!isPublicView ? [
      { key: 'unlocked', label: 'Unlocked', count: challenges.filter(c => c.user_unlocked && !c.user_completed).length },
      { key: 'completed', label: 'Completed', count: challenges.filter(c => c.user_completed).length },
    ] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Clear Progress Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md mx-4 shadow-2xl border border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-900/50 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Clear Quest Progress?</h3>
                <p className="text-sm text-slate-400">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-slate-300 mb-6">
              This will permanently delete all your submissions and reset your challenge progress.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClearProgress}
                disabled={clearing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                {clearing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Clear All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Header */}
      {stats && (
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">System Design Challenges</h2>
                <p className="text-purple-200 text-sm">{stats.total} challenges available</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {!isPublicView && (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Reset
                </button>
              )}
              {!isPublicView && (
                <div className="text-right">
                  <div className="text-3xl font-bold">{progressPercent}%</div>
                  <div className="text-purple-200 text-sm">Complete</div>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar - only show for authenticated users */}
          {!isPublicView && (
            <div className="h-3 bg-white/20 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full transition-all duration-1000"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}

          {!isPublicView ? (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-xs text-purple-200">Total</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-2xl font-bold">{stats.unlocked}</div>
                <div className="text-xs text-purple-200">Unlocked</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-2xl font-bold">{stats.completed}</div>
                <div className="text-xs text-purple-200">Completed</div>
              </div>
            </div>
          ) : (
            <p className="text-purple-200 text-sm text-center mt-2">
              Sign up to track your progress and unlock all challenges
            </p>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Filter</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterButtons.map(btn => (
            <button
              key={btn.key}
              onClick={() => setFilter(btn.key)}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2
                ${filter === btn.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }
              `}
            >
              {btn.color && <div className={`w-2 h-2 rounded-full ${btn.color}`} />}
              {btn.label}
              <span className={`text-xs ${filter === btn.key ? 'text-blue-200' : 'text-slate-500'}`}>
                ({btn.count})
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Challenge Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredChallenges.map(challenge => (
          <ChallengeTile
            key={challenge.id}
            challenge={challenge}
            isCompleted={challenge.user_completed || false}
            isUnlocked={challenge.user_unlocked || false}
            bestScore={challenge.user_best_score || 0}
            onSelect={handleChallengeSelect}
            isPremiumLocked={isChallengePremiumLocked(challenge)}
            isPublicView={isPublicView}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredChallenges.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-slate-600" />
          </div>
          <h3 className="text-lg font-medium text-slate-400 mb-2">No challenges found</h3>
          <p className="text-sm text-slate-500">Try a different filter</p>
        </div>
      )}

      {/* Legend */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
        <h3 className="font-medium text-white mb-3 text-sm">Legend</h3>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-slate-400">Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
              <Circle className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-slate-400">Unlocked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center">
              <Lock className="w-3 h-3 text-slate-400" />
            </div>
            <span className="text-slate-400">Locked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
              <Crown className="w-3 h-3 text-white" />
            </div>
            <span className="text-slate-400">Premium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-slate-400">Beginner (Free)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-slate-400">Intermediate</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-slate-400">Advanced</span>
          </div>
        </div>
      </div>

      {/* Premium Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradePromptModal onClose={() => setShowUpgradeModal(false)} />
      )}
    </div>
  );
};

export default ProgressTree;
